import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const COLS = 9, ROWS = 11, CELL = 24, W = COLS * CELL, H = ROWS * CELL;
const ROW_CFG = [
  null, // 0 goal
  { dir: 1, speed: 0.03, len: 2, gap: 4, c: "#ef4444" },
  { dir: -1, speed: 0.045, len: 1, gap: 3, c: "#e8b04b" },
  { dir: 1, speed: 0.055, len: 2, gap: 5, c: "#a78bfa" },
  null, // 4 safe median
  { dir: -1, speed: 0.04, len: 1, gap: 3, c: "#22d3ee" },
  { dir: 1, speed: 0.05, len: 2, gap: 4, c: "#f97316" },
  { dir: -1, speed: 0.06, len: 1, gap: 3, c: "#ef4444" },
  { dir: 1, speed: 0.035, len: 2, gap: 5, c: "#e8b04b" },
  null, // 9 safe
  null, // 10 start
];

export default function FrogCrossing() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const livesRef = useRef(3);
  const [status, setStatus] = useState("idle");

  const buildCars = () => {
    const cars = [];
    ROW_CFG.forEach((cfg, row) => { if (!cfg) return; for (let x = 0; x < COLS; x += cfg.len + cfg.gap) cars.push({ row, x: x + (row % 2), dir: cfg.dir, speed: cfg.speed, len: cfg.len, c: cfg.c }); });
    return cars;
  };
  const start = () => { g.current = { fx: 4, fy: ROWS - 1, cars: buildCars() }; scoreRef.current = 0; setScore(0); livesRef.current = 3; setLives(3); setStatus("playing"); sfx.beep(); };
  const hop = (dx, dy) => {
    const s = g.current; if (!s || status !== "playing") return;
    s.fx = Math.max(0, Math.min(COLS - 1, s.fx + dx)); s.fy = Math.max(0, Math.min(ROWS - 1, s.fy + dy)); sfx.eat();
    if (s.fy === 0) { scoreRef.current += 50; setScore(scoreRef.current); sfx.coin(); s.fx = 4; s.fy = ROWS - 1; }
  };

  useEffect(() => {
    const kd = (e) => {
      if (["ArrowUp", "w"].includes(e.key)) hop(0, -1);
      else if (["ArrowDown", "s"].includes(e.key)) hop(0, 1);
      else if (["ArrowLeft", "a"].includes(e.key)) hop(-1, 0);
      else if (["ArrowRight", "d"].includes(e.key)) hop(1, 0);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", kd);
    return () => window.removeEventListener("keydown", kd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      for (let r = 0; r < ROWS; r++) { ctx.fillStyle = r === 0 ? "#14532d" : ROW_CFG[r] ? "#2b2b33" : "#3f6f3a"; ctx.fillRect(0, r * CELL, W, CELL); }
      const s = g.current;
      (s.cars || []).forEach((car) => { ctx.fillStyle = car.c; ctx.fillRect(car.x * CELL + 2, car.row * CELL + 3, car.len * CELL - 4, CELL - 6); });
      ctx.fillStyle = "#22c55e"; ctx.fillRect((s.fx || 0) * CELL + 4, (s.fy || 0) * CELL + 4, CELL - 8, CELL - 8);
      ctx.fillStyle = "#052e16"; ctx.fillRect((s.fx || 0) * CELL + 7, (s.fy || 0) * CELL + 7, 3, 3); ctx.fillRect((s.fx || 0) * CELL + CELL - 10, (s.fy || 0) * CELL + 7, 3, 3);
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      for (const car of s.cars) { car.x += car.dir * car.speed; if (car.x > COLS) car.x = -car.len; if (car.x < -car.len) car.x = COLS; }
      for (const car of s.cars) { if (car.row === s.fy && s.fx + 0.6 > car.x && s.fx < car.x + car.len - 0.1) { livesRef.current -= 1; setLives(livesRef.current); sfx.explode(); if (livesRef.current <= 0) { cancelAnimationFrame(raf); setStatus("over"); return; } s.fx = 4; s.fy = ROWS - 1; break; } }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2 flex gap-2"><AScore label="Score" value={score} /><AScore label="Lives" value={"❤".repeat(lives) || "0"} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[216px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-2">
            {status === "over" && <AResult win={false} text={`Splat! · ${score}`} testId="frog-over" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 text-center text-xs text-kirana-cream/70">Hop to the top, dodge traffic</p>
      <div className="mt-1 grid w-max grid-cols-3 gap-1.5">
        <span /><PadButton testId="frog-up" onPress={() => hop(0, -1)}><ArrowUp className="h-5 w-5" /></PadButton><span />
        <PadButton testId="frog-left" onPress={() => hop(-1, 0)}><ArrowLeft className="h-5 w-5" /></PadButton>
        <span />
        <PadButton testId="frog-right" onPress={() => hop(1, 0)}><ArrowRight className="h-5 w-5" /></PadButton>
        <span /><PadButton testId="frog-down" onPress={() => hop(0, 1)}><ArrowDown className="h-5 w-5" /></PadButton><span />
      </div>
    </div>
  );
}
